import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import Calculator from './components/Calculator';
import Dashboard from './components/Dashboard';
import ProfileSettings from './components/ProfileSettings';
import Auth from './components/Auth';
import ThemeToggle from './components/ThemeToggle';
import { authService } from './services/authService';
import { profileService } from './services/profileService';
import { AuthUser } from './lib/supabase';
import { UserProfile } from './types';

import { useLocation } from 'react-router-dom';

const Navigation: React.FC = () => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const location = useLocation();

    const isActive = (path: string) => location.pathname === path;

    const navLinks = [
        { path: '/', icon: 'fa-calculator', label: 'Calculadora' },
        { path: '/historico', icon: 'fa-clock-rotate-left', label: 'Histórico' },
        { path: '/perfil', icon: 'fa-user-gear', label: 'Perfil' },
    ];

    return (
        <>
            {/* Header — Glassmorphism per design spec */}
            <nav className="glass ghost-border sticky top-0 z-50" style={{ borderTop: 'none', borderLeft: 'none', borderRight: 'none' }}>
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-14 sm:h-16 items-center">
                        <Link to="/" className="flex items-center gap-2.5">
                            <img src="/logo.png" alt="GM" className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl" />
                            <span className="text-lg sm:text-xl font-bold" style={{ color: 'var(--primary)', letterSpacing: '-0.01em' }}>
                                Gesso Milenio
                            </span>
                        </Link>

                        {/* Desktop Navigation */}
                        <div className="hidden md:flex items-center gap-1">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    className="px-4 py-2 text-sm font-semibold flex items-center gap-2 rounded-full transition-all"
                                    style={isActive(link.path)
                                        ? { color: 'var(--primary)', background: 'var(--primary-container)', opacity: 1 }
                                        : { color: 'var(--on-surface-variant)' }
                                    }
                                >
                                    <i className={`fa-solid ${link.icon}`}></i>
                                    <span>{link.label}</span>
                                </Link>
                            ))}
                            <div className="ml-2">
                                <ThemeToggle />
                            </div>
                        </div>

                        {/* Mobile Header Actions */}
                        <div className="flex md:hidden items-center gap-2">
                            <ThemeToggle />
                            <button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                className="p-2 rounded-xl transition-colors"
                                style={{ color: 'var(--on-surface-variant)', background: mobileMenuOpen ? 'var(--surface-container-low)' : 'transparent' }}
                                aria-label="Menu"
                            >
                                <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`}></i>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Dropdown Menu */}
                {mobileMenuOpen && (
                    <div className="md:hidden animate-fade-in" style={{ background: 'var(--surface-container-lowest)', borderTop: '1px solid rgba(169,174,177,0.12)' }}>
                        <div className="px-4 py-3 space-y-1">
                            {navLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className="flex items-center gap-3 px-4 py-3 rounded-2xl font-semibold transition-all"
                                    style={isActive(link.path)
                                        ? { color: 'var(--primary)', background: 'var(--primary-container)' }
                                        : { color: 'var(--on-surface-variant)' }
                                    }
                                >
                                    <i className={`fa-solid ${link.icon} text-lg w-6 text-center`}></i>
                                    <span>{link.label}</span>
                                    {isActive(link.path) && (
                                        <i className="fa-solid fa-check ml-auto" style={{ color: 'var(--primary)' }}></i>
                                    )}
                                </Link>
                            ))}
                        </div>
                    </div>
                )}
            </nav>

            {/* Bottom Navigation - Mobile Only — Glassmorphism */}
            <nav className="md:hidden fixed bottom-0 left-0 right-0 glass z-50 safe-area-bottom" style={{ borderBottom: 'none', borderLeft: 'none', borderRight: 'none', borderTop: '1px solid rgba(169,174,177,0.12)' }}>
                <div className="flex justify-around items-center h-16">
                    {navLinks.map((link) => (
                        <Link
                            key={link.path}
                            to={link.path}
                            className="flex flex-col items-center justify-center flex-1 h-full py-2 transition-all"
                            style={{ color: isActive(link.path) ? 'var(--primary)' : 'var(--on-surface-muted)' }}
                        >
                            <div className={`relative ${isActive(link.path) ? 'scale-110' : ''} transition-transform`}>
                                <i className={`fa-solid ${link.icon} text-xl`}></i>
                                {isActive(link.path) && (
                                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full" style={{ background: 'var(--secondary)' }}></span>
                                )}
                            </div>
                            <span className="text-xs mt-1 font-semibold">
                                {link.label}
                            </span>
                        </Link>
                    ))}
                </div>
            </nav>
        </>
    );
};


const App: React.FC = () => {
    const [user, setUser] = useState<AuthUser | null>(null);
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        // Verificar usuário atual
        const checkUser = async () => {
            const currentUser = await authService.getCurrentUser();
            setUser(currentUser);

            if (currentUser) {
                const userProfile = await profileService.getProfile(currentUser.id);
                setProfile(userProfile);
            }

            setLoading(false);
        };

        checkUser();

        // Listener para mudanças de autenticação
        const { data: { subscription } } = authService.onAuthStateChange(async (authUser) => {
            setUser(authUser);

            if (authUser) {
                const userProfile = await profileService.getProfile(authUser.id);
                setProfile(userProfile);
            } else {
                setProfile(null);
            }
        });

        return () => {
            subscription.unsubscribe();
        };
    }, []);

    const handleLogin = async (authUser: AuthUser) => {
        setUser(authUser);
        const userProfile = await profileService.getProfile(authUser.id);
        setProfile(userProfile);
    };

    const handleLogout = async () => {
        await authService.signOut();
        setUser(null);
        setProfile(null);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-5" style={{ background: 'linear-gradient(135deg, #001e42 0%, #005bae 100%)' }}>
                <img src="/logo.png" alt="GM" className="w-20 h-20 rounded-2xl animate-pulse p-2" style={{ background: '#ffffff', boxShadow: '0 20px 40px rgba(0,0,0,0.4)' }} />
                <div className="animate-spin rounded-full h-7 w-7 border-b-2" style={{ borderColor: '#a3fa00' }}></div>
            </div>
        );
    }

    if (!user) {
        return <Auth onLogin={handleLogin} />;
    }

    return (
        <Router>
            <div className="min-h-screen flex flex-col" style={{ background: 'var(--surface)' }}>
                <Navigation />
                <main className="flex-grow max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6 sm:py-8 pb-24 md:pb-8">
                    <Routes>
                        <Route path="/" element={<Calculator user={user} profile={profile} />} />
                        <Route path="/historico" element={<Dashboard user={user} />} />
                        <Route path="/perfil" element={<ProfileSettings user={user} profile={profile} setProfile={setProfile} onLogout={handleLogout} />} />
                    </Routes>
                </main>
                <footer className="hidden md:block py-5 mt-auto" style={{ background: 'var(--surface-container-lowest)', borderTop: '1px solid rgba(169,174,177,0.12)' }}>
                    <div className="max-w-5xl mx-auto px-4 flex items-center justify-center gap-2 text-sm" style={{ color: 'var(--on-surface-muted)' }}>
                        <img src="/logo.png" alt="GM" className="w-5 h-5 rounded-md" />
                        <span>© {new Date().getFullYear()} Gesso Milenio — A ferramenta essencial para o gesseiro moderno.</span>
                    </div>
                </footer>
            </div>
        </Router>
    );
};

export default App;
